import React from 'react';

/**
 * Top-level error boundary.
 *
 * Before this, any render error in any page (a bad prop, a crash inside a
 * Radix portal, an i18n key that resolved to something the JSX couldn't use)
 * bubbled to the React root and left the whole PWA on a completely blank
 * white page — no way to know what broke or to recover. That was how the
 * product-add modal's Usage-tab crash surfaced.
 *
 * Now we catch it, log to the console (so it lands in the browser devtools
 * for whoever's debugging), and show a compact recovery card with a
 * "reload" button. Users get out of the dead state; developers get a stack
 * trace instead of guessing.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message = this.state.error?.message || String(this.state.error);
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#fafafa',
          color: '#111',
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            padding: '28px 24px',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#fff',
            boxShadow: '0 4px 14px rgba(0,0,0,0.04)',
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
            Something went wrong
          </h1>
          <p style={{ margin: '0 0 12px', color: '#4b5563', fontSize: 14 }}>
            The page hit an unexpected error. Reload to try again; if it keeps
            happening, share the details below with the Farm Africa team.
          </p>
          <pre
            style={{
              margin: '0 0 16px',
              padding: 12,
              borderRadius: 8,
              background: '#f3f4f6',
              color: '#111',
              fontSize: 12,
              lineHeight: 1.4,
              overflow: 'auto',
              maxHeight: 200,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {message}
          </pre>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#166534',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
